import React, { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu as AntdMenu } from 'antd';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { isUserPremium } from '@investown/fe/ui-utils/properties';
import {
  useHasPermissionToInviteThroughReferralResult,
  UserVerificationStatus,
  useUserDetails,
} from '@investown/fe/api-sdk';

import { PATH_APP, PATH_INVITE_FRIEND, PATH_USER_PROFILE } from 'routes/routesPaths';
import SupportButton from 'components/UserMenu/SupportButton';
import LogoutButton from 'components/UserMenu/LogoutButton';
import PersonCircleOutlineIcon from 'components/Icons/PersonCircleOutlineIcon';
import PersonCircleIcon from 'components/Icons/PersonCircleIcon';
import ChatOutlineIcon from 'components/Icons/ChatOutlineIcon';
import LogoutOutlineIcon from 'components/Icons/LogoutOutlineIcon';
import { WalletBalance } from 'containers/Topbar/WalletBalance';
import MenuItem from 'components/Menu/MenuItem';
import MenuItemContainer from 'components/Menu/MenuItemContainer';
import MenuItemContent from 'components/Menu/MenuItemContent';
import AddPersonOutline from 'components/Icons/AddPersonOutline';
import AddPerson from 'components/Icons/AddPerson';
import SkeletonRow from 'components/SkeletonRow';
import PremiumIconFaded from 'components/Icons/PremiumIconFaded';
import PremiumIcon from 'components/Icons/PremiumIcon';
import UserDisplayName from 'components/UserDisplayName';
import MobileOutlineIcon from 'components/Icons/MobileOutlineIcon';
import MobileIcon from 'components/Icons/MobileIcon';

const MobileMenu: FC = () => {
  const { pathname } = useLocation();
  const { formatMessage } = useIntl();
  const userDetailsQueryResult = useUserDetails();
  const hasPermissionToInviteThroughReferralResult = useHasPermissionToInviteThroughReferralResult();

  const isLoading =
    userDetailsQueryResult.isLoading ||
    userDetailsQueryResult.isIdle ||
    hasPermissionToInviteThroughReferralResult.isLoading ||
    hasPermissionToInviteThroughReferralResult.isIdle;

  return (
    <MiddleMenu>
      <AntdMenu selectedKeys={[pathname]} forceSubMenuRender={false}>
        <MenuItem key={PATH_USER_PROFILE}>
          <Link to={PATH_USER_PROFILE}>
            <MenuItemContainer>
              {(userDetailsQueryResult.isLoading || userDetailsQueryResult.isIdle) && (
                <MenuItemContent
                  icon={<SkeletonRow width={25} />}
                  activeIcon={<SkeletonRow width={25} />}
                  active={pathname === PATH_USER_PROFILE}
                  content={<SkeletonRow width={70} />}
                />
              )}
              {userDetailsQueryResult.isError && (
                <MenuItemContent
                  icon={<PersonCircleOutlineIcon />}
                  activeIcon={<PersonCircleIcon />}
                  active={pathname === PATH_USER_PROFILE}
                  content={<UserDisplayName firstNameOnly />}
                />
              )}
              {userDetailsQueryResult.isSuccess && (
                <MenuItemContent
                  icon={
                    isUserPremium(userDetailsQueryResult.data) ? (
                      <PremiumIconFaded width="25px" />
                    ) : (
                      <PersonCircleOutlineIcon />
                    )
                  }
                  activeIcon={
                    isUserPremium(userDetailsQueryResult.data) ? <PremiumIcon width="25px" /> : <PersonCircleIcon />
                  }
                  active={pathname === PATH_USER_PROFILE}
                  content={<UserDisplayName firstNameOnly />}
                />
              )}
            </MenuItemContainer>
          </Link>
        </MenuItem>

        {!isLoading &&
          userDetailsQueryResult.data?.verificationStatus === UserVerificationStatus.Verified &&
          hasPermissionToInviteThroughReferralResult.data?.hasPermissionToInviteThroughReferral === true && (
            <MenuItem key={PATH_INVITE_FRIEND}>
              <Link to={PATH_INVITE_FRIEND}>
                <MenuItemContainer>
                  <MenuItemContent
                    icon={<AddPersonOutline />}
                    activeIcon={<AddPerson />}
                    active={pathname === PATH_INVITE_FRIEND}
                    content={formatMessage({ id: 'navbar.inviteFriends' })}
                  />
                </MenuItemContainer>
              </Link>
            </MenuItem>
          )}

        <MenuItem>
          <MenuItemContainer>
            <WalletBalance />
          </MenuItemContainer>
        </MenuItem>

        <MenuItem key={PATH_APP}>
          <Link to={PATH_APP}>
            <MenuItemContainer>
              <MenuItemContent
                icon={<MobileOutlineIcon color="transparent" />}
                activeIcon={<MobileIcon />}
                active={pathname === PATH_APP}
                content={formatMessage({ id: 'navbar.app' })}
              />
            </MenuItemContainer>
          </Link>
        </MenuItem>

        <MenuItem>
          <MenuItemContainer>
            <MenuItemContent icon={<ChatOutlineIcon />} content={<SupportButton />} />
          </MenuItemContainer>
        </MenuItem>

        <MenuItem>
          <MenuItemContainer>
            <MenuItemContent icon={<LogoutOutlineIcon />} content={<LogoutButton />} />
          </MenuItemContainer>
        </MenuItem>
      </AntdMenu>
    </MiddleMenu>
  );
};

const MiddleMenu = styled.div`
  ul.ant-menu li.ant-menu-item {
    margin: 0;
    padding-left: ${(props) => props.theme.paddings.medium} !important;
    padding-right: ${(props) => props.theme.paddings.medium} !important;
  }
`;

export default MobileMenu;
